import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Separator } from '@/components/ui/separator';
import { Users, Plus, Calendar, AlertTriangle, BarChart3, Network, Edit, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { LinkManager } from '@/components/shared/LinkManager';
import { SupplierEditDialog } from '@/components/suppliers/SupplierEditDialog';
import { SupplierCreateDialog } from '@/components/suppliers/SupplierCreateDialog';
import { useSuppliers } from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';

const criticalityColors = {
  low: 'bg-compliant text-compliant-foreground',
  medium: 'bg-warning text-warning-foreground',
  high: 'bg-destructive text-destructive-foreground',
  critical: 'bg-destructive text-destructive-foreground',
};

const criticalityLabels = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

export default function SuppliersPage() {
  const { organization } = useAuth();
  const [criticalityFilter, setCriticalityFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [selectedSupplierId, setSelectedSupplierId] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const {
    suppliers,
    isLoading,
    createSupplier,
    updateSupplier,
    deleteSupplier,
    isCreating,
    isDeleting,
  } = useSuppliers(organization?.id, { criticality: criticalityFilter !== 'all' ? criticalityFilter : undefined, category: categoryFilter !== 'all' ? categoryFilter : undefined });

  const selectedSupplier = suppliers?.find(s => s.id === selectedSupplierId);

  const handleCreate = async (data: any, links?: Array<{id: string; type: any; name: string}>) => {
    if (!organization?.id) return;
    
    createSupplier({
      ...data,
      org_id: organization.id,
    }, {
      onSuccess: async (newSupplier: any) => {
        if (newSupplier?.id && links && links.length > 0) {
          const { useLinkStore } = await import('@/stores/linkStore');
          const { bulkCreateLinks } = useLinkStore.getState();
          
          for (const link of links) {
            await bulkCreateLinks('supplier', newSupplier.id, link.type, [link.id]);
          }
        }
      }
    });

    setAddDialogOpen(false);
  };

  const handleDelete = () => {
    if (!selectedSupplierId) return;
    deleteSupplier(selectedSupplierId);
    setDeleteDialogOpen(false);
    setSelectedSupplierId(null);
  };

  const uniqueCategories = suppliers ? [...new Set(suppliers.map(s => s.category).filter(Boolean) as string[])] : [];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Suppliers</h1>
            <p className="text-muted-foreground">Manage third-party suppliers and vendors</p>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
              </CardHeader>
              <CardContent>
                <div className="h-3 bg-muted rounded w-full mb-2"></div>
                <div className="h-3 bg-muted rounded w-2/3"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Suppliers</h1>
          <p className="text-muted-foreground">Manage third-party suppliers and vendors</p>
        </div>
        <Button className="gap-2" onClick={() => setAddDialogOpen(true)}>
          <Plus className="h-4 w-4" />
          Add Supplier
        </Button>
      </div>

      <div className="flex gap-4">
        <Select value={criticalityFilter} onValueChange={setCriticalityFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by criticality" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Criticality Levels</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>

        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {uniqueCategories.map((category) => (
              <SelectItem key={category} value={category}>
                {category}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {suppliers && suppliers.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {suppliers.map((supplier) => (
            <Card 
              key={supplier.id}
              className="cursor-pointer hover:bg-accent/5 transition-colors"
              onClick={() => setSelectedSupplierId(supplier.id)}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-primary" />
                    {supplier.name}
                  </CardTitle>
                  <Badge className={criticalityColors[supplier.criticality as keyof typeof criticalityColors]}>
                    {criticalityLabels[supplier.criticality as keyof typeof criticalityLabels]}
                  </Badge>
                </div>
                {supplier.category && (
                  <CardDescription>{supplier.category}</CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Issues</span>
                    <div className="flex items-center gap-1">
                      {(supplier.issues_count || 0) > 0 && <AlertTriangle className="h-4 w-4 text-destructive" />}
                      <span className={(supplier.issues_count || 0) > 0 ? 'text-destructive font-medium' : ''}>
                        {supplier.issues_count || 0}
                      </span>
                    </div>
                  </div>

                  {supplier.questionnaire_score && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Score</span>
                      <div className="flex items-center gap-1">
                        <BarChart3 className="h-4 w-4 text-primary" />
                        <span className="font-medium">{supplier.questionnaire_score}/100</span>
                      </div>
                    </div>
                  )}

                  {supplier.next_review && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Next Review</span>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span>{format(new Date(supplier.next_review), 'MMM dd, yyyy')}</span>
                      </div>
                    </div>
                  )}

                  {supplier.last_assessment && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Last Assessment</span>
                      <span>{format(new Date(supplier.last_assessment), 'MMM dd, yyyy')}</span>
                    </div>
                  )}

                  {supplier.notes && (
                    <div className="pt-2 border-t">
                      <p className="text-sm text-muted-foreground">{supplier.notes}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No suppliers found</h3>
            <p className="text-muted-foreground text-center mb-4">
              Start managing your supply chain by adding supplier information.
            </p>
            <Button className="gap-2" onClick={() => setAddDialogOpen(true)}>
              <Plus className="h-4 w-4" />
              Add Your First Supplier
            </Button>
          </CardContent>
        </Card>
      )}

      <SupplierCreateDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onSave={handleCreate}
        isCreating={isCreating}
      />

      <Sheet open={!!selectedSupplierId} onOpenChange={(open) => !open && setSelectedSupplierId(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <div className="flex items-center justify-between">
              <SheetTitle>Supplier Details</SheetTitle>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditDialogOpen(true)}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDeleteDialogOpen(true)}
                  disabled={isDeleting}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          </SheetHeader>

          {selectedSupplier && (
            <>
              <div className="space-y-6 py-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-5 w-5 text-primary" />
                    <h3 className="text-lg font-semibold">{selectedSupplier.name}</h3>
                  </div>
                  <Badge className={criticalityColors[selectedSupplier.criticality as keyof typeof criticalityColors]}>
                    {criticalityLabels[selectedSupplier.criticality as keyof typeof criticalityLabels]}
                  </Badge>
                  {selectedSupplier.category && (
                    <p className="text-sm text-muted-foreground mt-2">{selectedSupplier.category}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Issues Count</p>
                    <div className="flex items-center gap-1 mt-1">
                      {(selectedSupplier.issues_count || 0) > 0 && <AlertTriangle className="h-4 w-4 text-destructive" />}
                      <span className={(selectedSupplier.issues_count || 0) > 0 ? 'text-destructive font-medium' : 'font-medium'}>
                        {selectedSupplier.issues_count || 0}
                      </span>
                    </div>
                  </div>

                  {selectedSupplier.questionnaire_score && (
                    <div>
                      <p className="text-sm text-muted-foreground">Questionnaire Score</p>
                      <div className="flex items-center gap-1 mt-1">
                        <BarChart3 className="h-4 w-4 text-primary" />
                        <span className="font-medium">{selectedSupplier.questionnaire_score}/100</span>
                      </div>
                    </div>
                  )}
                </div>

                {(selectedSupplier.next_review || selectedSupplier.last_assessment) && (
                  <div className="space-y-2">
                    {selectedSupplier.next_review && (
                      <div>
                        <p className="text-sm text-muted-foreground">Next Review</p>
                        <div className="flex items-center gap-1 mt-1">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{format(new Date(selectedSupplier.next_review), 'MMM dd, yyyy')}</span>
                        </div>
                      </div>
                    )}

                    {selectedSupplier.last_assessment && (
                      <div>
                        <p className="text-sm text-muted-foreground">Last Assessment</p>
                        <span className="text-sm">{format(new Date(selectedSupplier.last_assessment), 'MMM dd, yyyy')}</span>
                      </div>
                    )}
                  </div>
                )}

                {selectedSupplier.notes && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">Notes</p>
                    <p className="text-sm">{selectedSupplier.notes}</p>
                  </div>
                )}

                <Separator />

                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Network className="h-4 w-4 text-muted-foreground" />
                    <h4 className="text-sm font-medium">Related Entities</h4>
                  </div>
                  <LinkManager 
                    entityType="supplier"
                    entityId={selectedSupplier.id}
                    entityName={selectedSupplier.name}
                    showHeader={false}
                    allowedTypes={['asset', 'system', 'risk', 'control', 'evidence', 'incident', 'task']}
                  />
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <SupplierEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        supplier={selectedSupplier}
        onSave={(id, data) => updateSupplier({ id, data })}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Supplier</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedSupplier?.name}"? This action cannot be undone and will remove all associated links and access relations.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}